/**
 * Pop-up store chrome — wraps a brand's template demo in storefront UI.
 *
 * Rendered for storefront routes (/luma, /kinetic, /form):
 *   top:    sticky brand bar — logo, live drop countdown, cart stub
 *   bottom: manifesto + footer
 *
 * The 3D template (pop/editorial) mounts between them via main.js, embedded.
 * The countdown is the "pop-up" mechanic — a real urgency timer per drop.
 */
import { el } from './core/utils.js';

function pad(n) { return String(n).padStart(2, '0'); }

function startCountdown(node, endsInHours) {
  // Anchor the deadline to load time + N hours. (Demo: resets each visit.
  // For a real drop, store an absolute ISO deadline in the brand config.)
  const deadline = Date.now() + endsInHours * 3600 * 1000;
  let raf = 0;
  function tick() {
    const ms = Math.max(0, deadline - Date.now());
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    node.textContent = d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`;
    raf = requestAnimationFrame(() => setTimeout(tick, 250));
  }
  tick();
  return () => cancelAnimationFrame(raf);
}

export function mountStoreChrome(top, bottom, brand) {
  const drop = brand.drop || {};

  // ── Top bar ──
  const countdownEl = el('span', { className: 'store-countdown__time' }, '—');
  const bar = el('header', { className: 'store-bar' },
    el('a', { className: 'store-logo', href: '#' }, brand.name),
    el('div', { className: 'store-countdown' },
      el('span', { className: 'store-countdown__label' }, (drop.label || 'Drop') + ' ends in'),
      countdownEl,
    ),
    el('div', { className: 'store-bar__actions' },
      el('a', { className: 'store-cart', href: '#shop' }, 'Cart (0)'),
    ),
  );
  top.appendChild(bar);

  if (drop.endsInHours) startCountdown(countdownEl, drop.endsInHours);

  // ── Bottom: manifesto + footer ──
  const manifestoTitle = el('h2', { className: 'store-manifesto__title' });
  (brand.manifesto?.title || '').split('\n').forEach((line, i, arr) => {
    manifestoTitle.appendChild(document.createTextNode(line));
    if (i < arr.length - 1) manifestoTitle.appendChild(document.createElement('br'));
  });

  const manifesto = el('section', { className: 'store-manifesto', id: 'shop' },
    manifestoTitle,
    el('p', { className: 'store-manifesto__body' }, brand.manifesto?.body || ''),
    el('a', { className: 'store-cta', href: '#' }, brand.hero?.cta || 'Shop the drop'),
  );

  const footer = el('footer', { className: 'store-footer' },
    el('div', {}, '© ' + brand.name + ' · pop-up by '),
    el('a', { href: '/', className: 'store-pop3d-credit' }, 'POP3D'),
  );

  bottom.appendChild(manifesto);
  bottom.appendChild(footer);
}
